import { z } from 'zod';
import { listInput } from './list';
import { idempotency, toJobFields } from './spine';
import { lineItemInput } from './money';

const optionalText = (max: number) =>
  z.string().trim().max(max).optional().or(z.literal(''));

export const requestCreateInput = idempotency.extend({
  contactName: z.string().trim().min(1).max(200),
  contactEmail: z.string().trim().email().max(200).optional(),
  contactPhone: z.string().trim().max(40).optional(),
  addressText: optionalText(300),
  title: z.string().trim().min(1).max(200),
  details: optionalText(4000),
  urgency: z.enum(['low', 'normal', 'emergency']).optional(),
  preferredWindow: optionalText(120),
  source: z.string().trim().max(40).optional(),
  customerId: z.string().uuid().optional(),
  propertyId: z.string().uuid().optional(),
});
export type RequestCreateInput = z.infer<typeof requestCreateInput>;

const byRequest = idempotency.extend({ requestId: z.string().uuid() });
export const requestApproveToCommercialJobInput = byRequest
  .extend({
    pipelineId: z.string().uuid().optional(),
    stageId: z.string().uuid().optional(),
    ownerUserId: z.string().uuid().optional(),
    serviceTypeId: z.string().uuid().optional(),
  })
  .strict();
export type RequestApproveToCommercialJobInput = z.infer<
  typeof requestApproveToCommercialJobInput
>;

/**
 * Approve straight into a job, with the service type the office picks as it approves (ticket
 * 05); a start time schedules its first visit in one step: `toJobFields`.
 */
export const requestApproveToJobInput = byRequest.extend(toJobFields);
export type RequestApproveToJobInput = z.infer<typeof requestApproveToJobInput>;

/** Approve into a quote. Lines are required — a quote with nothing on it is not a quote. */
export const requestApproveToQuoteInput = byRequest.extend({
  taxRateBps: z.coerce.number().int().min(0).max(20_000).optional(),
  lines: z.array(lineItemInput).min(1).max(200),
});
export type RequestApproveToQuoteInput = z.infer<typeof requestApproveToQuoteInput>;

export const requestDeclineInput = byRequest.extend({
  reason: optionalText(500),
});
export type RequestDeclineInput = z.infer<typeof requestDeclineInput>;

/**
 * The requests screen's two tabs are a status filter, not a client-side split of a
 * downloaded array — see listRequests. `triaged` absent means "both".
 */
export const requestListInput = listInput.extend({
  triaged: z.boolean().optional(),
});
export type RequestListInput = z.infer<typeof requestListInput>;
